import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/useSession";
import { usePortalUser } from "@/hooks/usePortalUser";
import { PortalUserContext } from "@/admin/PortalUserContext";
import { scopedPortalAssignments, workspacesFromGrants } from "@/lib/portalSession";

export function AdminProtected({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const { session, loading } = useSession();
  const { portalSession, checking, isPortalUser } = usePortalUser(session);
  const [activeAssignmentId, setActiveAssignmentId] = useState<string | null>(null);

  const grantsDigest = useMemo(
    () => portalSession?.grants.map((g) => `${g.id}:${g.capability}`).sort().join("|") ?? "",
    [portalSession?.grants]
  );
  const workspaces = useMemo(() => {
    if (!portalSession) return [];
    return workspacesFromGrants(portalSession.grants);
  }, [portalSession, grantsDigest]);

  useEffect(() => {
    if (!workspaces.length) {
      setActiveAssignmentId(null);
      return;
    }
    const scoped = scopedPortalAssignments(workspaces);
    if (scoped.length === 0) {
      setActiveAssignmentId(null);
      return;
    }
    setActiveAssignmentId((prev) => {
      if (prev && scoped.some((s) => s.id === prev)) return prev;
      return scoped.sort((a, b) => a.created_at.localeCompare(b.created_at))[0]?.id ?? null;
    });
  }, [workspaces]);

  useEffect(() => {
    if (loading) return;
    if (!session) {
      navigate("/admin/login", { replace: true });
    }
  }, [loading, session, navigate]);

  if (loading || !session) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-gray text-brand-blue">
        Loading…
      </div>
    );
  }

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-gray text-brand-blue">
        Verifying access…
      </div>
    );
  }

  if (!isPortalUser || !portalSession) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-brand-gray px-4 text-center">
        <p className="font-display text-xl font-bold text-brand-blue">
          Access not authorised
        </p>
        <p className="mt-3 max-w-md text-sm text-brand-blue/80">
          {session.user.email} is not registered in the portal access list. Ask a super user to add
          your account.
        </p>
        <button
          type="button"
          onClick={() => void supabase.auth.signOut()}
          className="mt-8 rounded-full bg-brand-maroon px-8 py-3 text-sm font-bold uppercase text-white"
        >
          Sign out
        </button>
        <Link to="/" className="mt-6 text-sm font-semibold text-brand-maroon">
          Home
        </Link>
      </div>
    );
  }

  return (
    <PortalUserContext.Provider
      value={{
        email: portalSession.email,
        grants: portalSession.grants,
        assignments: workspaces,
        activeAssignmentId,
        setActiveAssignmentId,
      }}
    >
      {children}
    </PortalUserContext.Provider>
  );
}
